import {usePlasseringer, Plassering} from "../../api/plasseringer";
import LoadingSpinner from "../../components/LoadingSpinner";
import {PktAlert, PktButton, PktDatepicker, PktTextarea} from "@oslokommune/punkt-react";
import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {FormProvider, SubmitHandler, useForm, useFormContext} from "react-hook-form";
import {submitError} from "../../errorReport";
import RadioButtons from "../../components/RadioButtons";
import {InfoBox, InfoBoxItem} from "../../components/InfoBox";
import {getDocumentTitle} from "../../utils/documentTitle";
import {dateIsoString, isoStringRangeToLocaleStringRange} from "../../utils/datoUtil";
import {ApiError} from "../../api/apiError";
import {PermisjonssøknadFormData, submitPermisjonssøknad} from "../../api/permisjon";

type SubmitState = undefined | 'pending' | 'completed' | 'error'

export function Permisjonssøknad() {
  const navigate = useNavigate()
  const [plasseringer, plasseringerStatus, plasseringerError] = usePlasseringer()
  const methods = useForm<PermisjonssøknadFormData>()
  const {handleSubmit, control, watch, resetField, setValue} = methods

  const valgtBarnIdent = watch('barnIdent')
  const valgtPlasseringId = watch('plasseringId')

  const [submitState, setSubmitState] = useState<SubmitState>(undefined)

  useEffect(() => {
    document.title = getDocumentTitle("permisjon")
  })

  useEffect(() => {
    //nullstill valgt barnehage hvis man bytter barn
    resetField('plasseringId')
  }, [valgtBarnIdent])

  const barnMedPlassering = useMemo(() => {
    if (plasseringer) {
      return plasseringer.reduce((acc, val) => {
        if (!acc.some((b) => b.ident == val.barnIdent)) {
          acc.push({ident: val.barnIdent, navn: [val.barnFornavn, val.barnEtternavn].join(' ')})
        }
        return acc
      }, [] as Array<{ ident: string, navn: string }>)
    } else {
      return []
    }
  }, [plasseringer])

  const plasseringerForValgtBarn = useMemo(() => {
    if (valgtBarnIdent && plasseringer) {
      return plasseringer.filter(plassering => plassering.barnIdent === valgtBarnIdent)
    } else {
      return []
    }
  }, [valgtBarnIdent, plasseringer])

  useEffect( ()=> {
    if(plasseringerForValgtBarn.length === 1) {
      setValue('plasseringId', plasseringerForValgtBarn[0].id)
    }
  }, [plasseringerForValgtBarn])

  const valgtPlassering = useMemo(() => {
    if (valgtPlasseringId && plasseringer) {
      return plasseringer.find(plassering => plassering.id == valgtPlasseringId)
    }
  }, [valgtPlasseringId, plasseringer])

  const barnFulltNavn = [valgtPlassering?.barnFornavn, valgtPlassering?.barnEtternavn].join(' ')
  const kanSendeInn = valgtPlassering && valgtPlassering.kanSøkePermisjon && !valgtPlassering.harSøktPermisjon

  const submitForm: SubmitHandler<PermisjonssøknadFormData> = (data) => {
    if(submitState === 'pending') return
    setSubmitState('pending')
    submitPermisjonssøknad(data).then(() => {
      setSubmitState('completed')
      navigate(`/permisjon/kvittering`, {
        state: {
          barnFulltNavn: barnFulltNavn,
        }
      })
    }).catch(err => {
      setSubmitState('error')
      console.error('Failed to submit permisjonssøknad', err)
      submitError('Failed to submit permisjonssøknad', err)
    })
  }

  function PlasseringerError() {
    if (plasseringerError instanceof ApiError && plasseringerError.type === 'PersonMedGradering') {
      return (
        <PktAlert skin={'info'} className={'mt-size-40'} style={{maxWidth: '45rem'}}>
          Vi kan ikke vise dine barnehageplasser her. Ta kontakt med oss på
          telefon <span style={{ whiteSpace: 'nowrap' }}>23 68 08 30</span> hvis du ønsker å si opp en plass.
        </PktAlert>
      )
    } else {
      return (
        <PktAlert skin={'error'} className={'mt-size-40'} style={{maxWidth: '45rem'}}>
          Det skjedde en feil. Vennligst prøv igjen senere.
        </PktAlert>
      )
    }
  }

  return (
    <FormProvider {...methods}>
      <div className="pkt-cell pkt-cell--span12">

        <h1 className={'pkt-txt-40'}>
          Søk om permisjon fra barnehagen
        </h1>
        {plasseringerStatus === 'pending' && (
          <LoadingSpinner/>
        )}
        {plasseringerStatus === 'error' && (
          <PlasseringerError/>
        )}
        {plasseringerStatus === 'ready' && (
          <>
            <form onSubmit={handleSubmit(submitForm)}>
              <RadioButtons
                control={control}
                className={'mt-size-30'}
                name={'barnIdent'}
                label={'Hvilket barn skal ha permisjon?'}
                options={barnMedPlassering.map(barn => ({label: barn.navn, value: barn.ident}))}
              />

              {barnMedPlassering.length === 0 && (
                <PktAlert skin={'info'} className={'mt-size-20'} style={{maxWidth: '50rem'}}>
                  Vi finner ingen barnehageplasser registrert på deg i Oslo kommune.
                </PktAlert>
              )}

              {valgtBarnIdent && plasseringerForValgtBarn.length > 1 && (
                <>
                  <RadioButtons
                    control={control}
                    name={'plasseringId'}
                    className={'mt-size-40'}
                    hasTile={false}
                    label={'Hvilken barnehageplass vil du søke permisjon fra?'}
                    options={plasseringerForValgtBarn.map(plassering => ({
                      label: plassering.barnehageNavn,
                      value: plassering.id.toString(),
                      helptext: isoStringRangeToLocaleStringRange(plassering.startDato, plassering.sluttDato)
                    }))}
                  />
                </>
              )}

              {valgtPlassering && valgtPlassering.harSøktPermisjon && (
                <PktAlert skin={"warning"} className={'mt-size-40'} style={{maxWidth: '50rem'}}>
                  Søknad om permisjon er allerede sendt inn for denne plassen
                </PktAlert>
              )}

              {valgtPlassering && !valgtPlassering.kanSøkePermisjon && !valgtPlassering.harSøktPermisjon && (
                <PktAlert skin={"warning"} className={'mt-size-40'} style={{maxWidth: '50rem'}}>
                  Kun fakturamottaker kan sende inn søknad om permisjon
                </PktAlert>
              )}

              {valgtPlassering && kanSendeInn && (
                <>
                  <Steg3 plassering={valgtPlassering} submitState={submitState}/>
                </>
              )}
            </form>
          </>
        )}

      </div>
    </FormProvider>
  )
}

function Steg3({plassering, submitState}: { plassering: Plassering, submitState: SubmitState }) {

  const {register, formState} = useFormContext<PermisjonssøknadFormData>()
  const barnFulltNavn = [plassering?.barnFornavn, plassering?.barnEtternavn].join(' ')
  const startDatoString = plassering?.startDato && new Date(plassering.startDato).toLocaleDateString('no', {dateStyle: 'long'})
  const sluttDatoString = plassering?.sluttDato && new Date(plassering.sluttDato).toLocaleDateString('no', {dateStyle: 'long'})

  const todayDateString = useMemo(() => {
    const now = new Date()
    return dateIsoString(now)
  }, [])

  return (
    <>
      <h2 className='pkt-txt-30 mt-size-40'>Tidspunkt for permisjonen</h2>
      <div style={{maxWidth: '50rem'}}>
        <p>
          Du kan søke om permisjon i inntil 1 år og permisjonstiden må avsluttes senest 31. juli. Hvis du ikke skal
          bruke plassen når permisjonen er ferdig, må du si opp plassen.
        </p>
        <p>
          For at plassen ikke skal stå ledig i permisjonsperioden, tildeler kommunen plassen til et annet barn som står
          på venteliste. Derfor er praksis å kun godkjenne permisjon hvis den avsluttes 31. juli. Etter permisjonen kan
          dere bli tilbudt plass i en annen barnehage dersom det ikke er ledige plasser i barnehagen dere har hatt
          permisjon fra.
        </p>
      </div>

      <InfoBox className={`mt-size-30 p-size-20`}>
        <InfoBoxItem title={'Navn'}>{barnFulltNavn}</InfoBoxItem>
        <InfoBoxItem title={'Barnehage'}>{plassering?.barnehageNavn}</InfoBoxItem>
        <InfoBoxItem title={'Plassering'}>{[startDatoString, sluttDatoString].filter(Boolean).join(' - ')}</InfoBoxItem>
        <InfoBoxItem title={'Plasstype'}>{plassering?.plasstype}</InfoBoxItem>
      </InfoBox>

      <PktDatepicker
        className={'mt-size-30'}
        label={"I hvilken periode ønsker dere permisjon?"}
        hasError={!!formState.errors.periode}
        errorMessage={formState.errors.periode?.message}
        min={todayDateString}
        range={true}
        {...register('periode', {
          required: "Dette feltet må fylles ut",
          min: {value: todayDateString, message: "Dato kan ikke være før i dag"},
          validate: {
            sluttDato: (input) => {
              if(input.split(",").length < 2) {
                return "Du må velge sluttdato for permisjonen"
              }
              return true
            }
          }
        })}
      />

      <PktTextarea
        {...register('begrunnelse', {
          required: "Dette feltet må fylles ut",
          maxLength: {value: 250, message: "Maks lengde er 250 tegn"}
        })}
        className={'mt-size-30'}
        hasError={!!formState.errors.begrunnelse}
        errorMessage={formState.errors.begrunnelse?.message}
        maxLength={250}
        counterMaxLength={250}
        counter={true}
        id={"begrunnelse-textarea"}
        label={"Hvorfor ønsker dere permisjon?"}
      >

      </PktTextarea>

      {submitState === 'error' && (
        <PktAlert skin={'error'} className={'mt-size-40'}>
          Det skjedde en feil ved innsending, prøv igjen senere.
        </PktAlert>
      )}

      <PktButton className={'mt-size-40 mb-size-100'} type={"submit"} isLoading={submitState === 'pending'}>
        Send inn søknad
      </PktButton>

    </>
  )
}
